if (!window.customElements.get('subscription-form')) {
    class SubscriptionForm extends HTMLElement {
        constructor() {
            super();
            this.productPage = this.closest('product-page') || null;
            this.sellingPlanInputs = this.querySelectorAll('input[name*=selling_plan].subscription-option__input');
            this.popupTrigger = this.querySelector('.subscription-option__benefits-more-link');
            this.isV3 = this.classList.contains('product__subscription-form--v3');
            this.sectionContainer = this.closest('[id^="shopify-section-"]') || null;
            this.contextRoot = this.productPage || this.sectionContainer || document;
            this.moneyFormat = window.KROWN?.settings?.shop_money_format || window.Shopify?.money_format || '${{amount}}';
            this.basePrices = this.getBasePrices();
            this.quantityInputs = [];
            this.servingsTargets = Array.from(this.querySelectorAll('[data-servings-number-target]'));
            this.baseServingsNumber = this.getBaseServingsNumber();
            this.sellingPlanSelect = this.querySelector('.subscription-option__select-input');
            this.defaultSellingPlanValue = this.sellingPlanSelect?.value || null;
            this.extendedSellingPlanValue = undefined;
            this.frequencyLabelWrapper = this.querySelector('.subscription-option__select-label');
            this.primaryFrequencyLabels = Array.from(document.querySelectorAll('[data-frequency-label]'));
            this.frequencyDefaultText = this.frequencyLabelWrapper?.dataset.frequencyDefault?.trim()
                || this.resolveFrequencyFallback(this.primaryFrequencyLabels, 'default')
                || '';
            this.frequencyExtendedText = this.frequencyLabelWrapper?.dataset.frequencyExtended?.trim()
                || this.resolveFrequencyFallback(this.primaryFrequencyLabels, 'extended')
                || '';
            this.frequencyLabelConfigs = this.getFrequencyLabelConfigs();
            this.quantityLabelTargets = this.getQuantityLabelTargets();
            this.handleQuantityChange = this.handleQuantityChange.bind(this);
        }

        connectedCallback() {
            this.initEventListeners();
            this.initQuantityListeners();
            this.initSubscribeBar();
            const initialPurchaseType = this.getCurrentPurchaseType();
            this.updateShippingIcons(initialPurchaseType);
            this.toggleAuxiliaryBlocks(initialPurchaseType);
            this.updateDisplayedPrices();
        }

        disconnectedCallback() {
            this.removeQuantityListeners();
        }

        initEventListeners() {
            this.sellingPlanInputs.forEach(input => input.addEventListener('change', this.handleInputChange.bind(this)));
            this.popupTrigger?.addEventListener('click', this.showPopup.bind(this));
        }

        initQuantityListeners() {
            if (!this.isV3 || !this.contextRoot) return;

            this.removeQuantityListeners();
            this.quantityInputs = Array.from(this.contextRoot.querySelectorAll('component-quantity-form input[name="quantity"]'));
            this.quantityInputs.forEach(input => input.addEventListener('change', this.handleQuantityChange));
        }

        removeQuantityListeners() {
            if (!this.quantityInputs?.length) return;

            this.quantityInputs.forEach(input => input.removeEventListener('change', this.handleQuantityChange));
        }

        handleInputChange(event) {
            const purchaseType = event.target.value === '' ? 'onetime' : 'subscription';

            this.updatePrice(purchaseType);
            this.updateShippingIcons(purchaseType);
            this.updateATCButtonText(purchaseType);
            this.toggleAuxiliaryBlocks(purchaseType);
            this.updateDisplayedPrices();
        }

        getCurrentPurchaseType() {
            const checkedInput = Array.from(this.sellingPlanInputs).find(input => input.checked);
            if (!checkedInput) return 'subscription';
            return checkedInput.value === '' ? 'onetime' : 'subscription';
        }

        updatePrice(purchaseType) {
            if (!this.productPage) return;

            const productPricesBlock = this.productPage.querySelector('.product-info');
            if (!productPricesBlock) return;

            const isSubscription = purchaseType === 'subscription';
            productPricesBlock.classList.toggle('product-info--subscribe', isSubscription);
        }

        updateShippingIcons(purchaseType) {
            if (!this.productPage) return;

            const subscribeOptionIconsWithTexts = this.productPage.querySelector('.subscribe-option_icons_with_texts');
            const onetimeOptionIconsWithTexts = this.productPage.querySelector('.onetime-option_icons_with_texts');
            if (!subscribeOptionIconsWithTexts || !onetimeOptionIconsWithTexts) return;

            const isSubscription = purchaseType === 'subscription';
            subscribeOptionIconsWithTexts?.classList.toggle('show', isSubscription);
			onetimeOptionIconsWithTexts?.classList.toggle('show', !isSubscription);
        }

        updateATCButtonText(purchaseType) {
            if (!this.productPage) return;

            const buttonText = this.productPage.querySelector('.js-button-text');
		    const subscribeBarButtonText = this.productPage.querySelector('.subscribe-bar .add-to-cart .button__text');
            const isSubscription = purchaseType === 'subscription';

            if (buttonText) buttonText.textContent = isSubscription ? 'Subscribe Now' : 'Try It Risk Free!';
            if (buttonText && this.classList.contains('product__subscription-form--enhanced')) buttonText.textContent = isSubscription ? 'Subscribe & Save' : 'Try It Risk Free!';
            if (subscribeBarButtonText) subscribeBarButtonText.textContent = isSubscription ? 'Subscribe Now' : 'Try It Risk Free!';
        }

        toggleAuxiliaryBlocks(purchaseType) {
            if (!this.isV3 || !this.contextRoot) return;

            const isSubscription = purchaseType === 'subscription';
            this.contextRoot?.setAttribute('data-subscription-state', purchaseType);

            const subscriptionBlocks = this.contextRoot.querySelectorAll('[data-subscription-form="subscription_form_v3"][data-subscription-visibility="subscription"]');
            const onetimeBlocks = this.contextRoot.querySelectorAll('[data-subscription-form="subscription_form_v3"][data-subscription-visibility="onetime"]');

            subscriptionBlocks.forEach(block => this.setBlockVisibility(block, isSubscription));
            onetimeBlocks.forEach(block => this.setBlockVisibility(block, !isSubscription));
        }

        setBlockVisibility(block, shouldShow) {
            if (!block) return;

            this.cacheOriginalDisplay(block);

            block.toggleAttribute('hidden', !shouldShow);
            block.setAttribute('aria-hidden', (!shouldShow).toString());
            block.style.display = shouldShow ? (block.dataset.originalDisplay || '') : 'none';
        }

        handleQuantityChange() {
            this.updateDisplayedPrices();
        }

        updateDisplayedPrices() {
            if (!this.isV3) return;

            const purchaseType = this.getCurrentPurchaseType();
            const quantityData = this.getSelectedQuantityData();
            this.renderSubscriptionPrices(quantityData);
            this.renderOnetimePrices(quantityData);
            this.renderSavings(quantityData);
            this.renderSummarySavings(quantityData, purchaseType);
            this.renderServingsCount(quantityData);
            this.renderQuantityLabels(quantityData);
            this.renderPricePerServing(quantityData);
            this.updatePriceEaLabels();
            this.updateSellingPlanForQuantity(quantityData);
        }

        getSelectedQuantityData() {
            const defaultQuantity = 1;
            const selectedInput = this.contextRoot?.querySelector('component-quantity-form input[name="quantity"]:checked');
            const parsedQuantity = parseInt(selectedInput?.value, 10);
            const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : defaultQuantity;
            const selectedLabel = selectedInput?.closest('.quantity-option');

            const baseOnetime = this.basePrices.priceOnetime || this.basePrices.priceSubscription || 0;
            const baseSubscription = this.basePrices.priceSubscription || baseOnetime;
            const baseCompareOnetime = this.basePrices.compareOnetime || baseOnetime;
            const baseCompareSubscription = this.basePrices.compareSubscription || baseCompareOnetime;
            let baseSavingsSubscription = this.basePrices.savingsSubscription || 0;

            if (!baseSavingsSubscription && baseCompareSubscription > baseSubscription) {
                baseSavingsSubscription = baseCompareSubscription - baseSubscription;
            }

            const multiply = (value) => {
                const numericValue = Number.isFinite(value) ? value : 0;
                return numericValue * quantity;
            };

            const parseDatasetValue = (attribute, fallback) => {
                if (!selectedLabel) return fallback;
                const parsed = parseInt(selectedLabel.dataset[attribute], 10);
                return Number.isFinite(parsed) ? parsed : fallback;
            };

            return {
                quantity,
                priceOnetime: parseDatasetValue('priceOnetime', multiply(baseOnetime)),
                compareOnetime: parseDatasetValue('compareOnetime', multiply(baseCompareOnetime)),
                priceSubscription: parseDatasetValue('priceSubscription', multiply(baseSubscription)),
                compareSubscription: parseDatasetValue('compareSubscription', multiply(baseCompareSubscription)),
                savingsSubscription: parseDatasetValue('savingsSubscription', multiply(baseSavingsSubscription)),
                pricePerServingOnetime: parseDatasetValue('pricePerServingOnetime', this.basePrices.pricePerServingOnetime),
                pricePerServingSubscription: parseDatasetValue('pricePerServingSubscription', this.basePrices.pricePerServingSubscription)
            };
        }

        renderServingsCount(data) {
            if (!this.isV3 || !this.servingsTargets.length) return;

            const baseServings = this.baseServingsNumber;
            if (!baseServings) return;

            const quantity = Number.isFinite(data?.quantity) && data.quantity > 0 ? data.quantity : 1;
            const totalServings = baseServings * quantity;

            if (!Number.isFinite(totalServings) || totalServings <= 0) return;

            const servingsText = totalServings.toString();
            this.servingsTargets.forEach(target => {
                target.textContent = servingsText;
            });
        }

        renderQuantityLabels(data) {
            if (!this.isV3 || !this.quantityLabelTargets?.length) return;

            const quantity = Number.isFinite(data?.quantity) && data.quantity > 0 ? data.quantity : 1;
            const quantityText = quantity.toString();

            this.quantityLabelTargets.forEach(target => {
                if (!target) return;
                target.textContent = quantityText;
            });
        }

        renderPricePerServing(data) {
            if (!this.isV3) return;

            const updateTargets = (selector, cents) => {
                if (!Number.isFinite(cents)) return;
                const targets = this.querySelectorAll(selector);
                if (!targets?.length) return;
                targets.forEach(target => this.setPriceText(target, cents));
            };

            const subscriptionPrice = Number.isFinite(data?.pricePerServingSubscription)
                ? data.pricePerServingSubscription
                : this.basePrices.pricePerServingSubscription;
            const onetimePrice = Number.isFinite(data?.pricePerServingOnetime)
                ? data.pricePerServingOnetime
                : this.basePrices.pricePerServingOnetime;

            updateTargets('[data-price-per-serving-subscription]', subscriptionPrice);
            updateTargets('[data-price-per-serving-onetime]', onetimePrice);
        }

        updatePriceEaLabels() {
            const targets = this.getPriceEaLabelTargets();
            if (!targets?.length) return;

            const selectedValue = this.getSelectedPriceEaValue();

            targets.forEach(target => {
                if (!target) return;

                const value = this.getPriceEaValueForTarget(target, selectedValue);
                if (!value) return;

                if (target.textContent !== value) {
                    target.textContent = value;
                }
            });
        }

        getPriceEaLabelTargets() {
            const scope = this.contextRoot?.querySelectorAll ? this.contextRoot : document;
            if (!scope?.querySelectorAll) return [];

            return Array.from(scope.querySelectorAll('[data-price-ea-label]'));
        }

        getPriceEaValueForTarget(target, fallbackValue) {
            if (!target) return null;

            const directValue = target.dataset.priceEa?.trim();
            if (directValue) return directValue;

            const closestSource = target.closest('[data-price-ea]');
            const closestValue = closestSource?.dataset.priceEa?.trim();
            if (closestValue) return closestValue;

            return fallbackValue || null;
        }

        getSelectedPriceEaValue() {
            const selectedInput = this.contextRoot?.querySelector('component-quantity-form input[name="quantity"]:checked');
            const selectedLabel = selectedInput?.closest('[data-price-ea]');
            const value = selectedLabel?.dataset.priceEa;

            return value?.trim() || null;
        }

        updateSellingPlanForQuantity(data) {
            if (!this.isV3) return;

            const needsExtendedPlan = data?.quantity === 4;
            this.updateFrequencyLabel(needsExtendedPlan);

            if (!this.sellingPlanSelect) return;

            if (!this.defaultSellingPlanValue) {
                this.defaultSellingPlanValue = this.sellingPlanSelect.value;
            }

            const targetValue = needsExtendedPlan ? this.getExtendedSellingPlanValue() : this.defaultSellingPlanValue;
            if (!targetValue || this.sellingPlanSelect.value === targetValue) return;

            this.sellingPlanSelect.value = targetValue;
            this.sellingPlanSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        updateFrequencyLabel(useExtended) {
            if (!this.frequencyLabelConfigs?.length) return;

            this.frequencyLabelConfigs.forEach(config => {
                const defaultText = config.defaultText || config.element.textContent || '';
                const extendedText = config.extendedText || defaultText;
                const nextText = useExtended ? extendedText : defaultText;

                if (nextText && config.element.textContent !== nextText) {
                    config.element.textContent = nextText;
                }
            });
        }

        resolveFrequencyFallback(labels, type) {
            if (!Array.isArray(labels)) return '';

            for (const label of labels) {
                if (!label) continue;

                const datasetValue = type === 'default'
                    ? label.dataset.frequencyDefault?.trim()
                    : label.dataset.frequencyExtended?.trim();

                if (datasetValue) {
                    return datasetValue;
                }

                if (type === 'default') {
                    const textValue = label.textContent?.trim();
                    if (textValue) {
                        return textValue;
                    }
                }
            }

            return '';
        }

        getExtendedSellingPlanValue() {
            if (this.extendedSellingPlanValue !== undefined) {
                return this.extendedSellingPlanValue;
            }

            if (!this.sellingPlanSelect) {
                this.extendedSellingPlanValue = null;
                return this.extendedSellingPlanValue;
            }

            const options = Array.from(this.sellingPlanSelect.options);
            if (!options.length) {
                this.extendedSellingPlanValue = null;
                return this.extendedSellingPlanValue;
            }

            const defaultValue = this.defaultSellingPlanValue || options[0].value;
            const defaultIndex = options.findIndex(option => option.value === defaultValue);
            const nextOption = defaultIndex === -1 ? options[1] : options[defaultIndex + 1];

            this.extendedSellingPlanValue = nextOption?.value || defaultValue;
            return this.extendedSellingPlanValue;
        }

        getFrequencyLabelConfigs() {
            const scopes = [this];
            if (this.contextRoot && this.contextRoot !== this) {
                scopes.push(this.contextRoot);
            }
            if (document && document !== this.contextRoot) {
                scopes.push(document);
            }

            const seen = new Set();
            const targets = [];

            scopes.forEach(scope => {
                if (!scope?.querySelectorAll) return;
                scope.querySelectorAll('[data-frequency-label]').forEach(element => {
                    if (seen.has(element)) return;
                    seen.add(element);
                    targets.push(element);
                });
            });

            if (!targets.length) return [];

            const fallbackDefault = this.frequencyDefaultText || 'every 30 days';
            const fallbackExtended = this.frequencyExtendedText || 'every 45 days' || fallbackDefault;

            return targets.map(element => {
                const wrapper = element.closest('[data-frequency-default]') || element;
                const defaultText = element.dataset.frequencyDefault?.trim()
                    || wrapper?.dataset?.frequencyDefault?.trim()
                    || element.textContent?.trim()
                    || fallbackDefault
                    || '';
                const extendedText = element.dataset.frequencyExtended?.trim()
                    || wrapper?.dataset?.frequencyExtended?.trim()
                    || fallbackExtended
                    || defaultText;

                return { element, defaultText, extendedText };
            });
        }

        getQuantityLabelTargets() {
            const scopes = [this];
            if (this.contextRoot && this.contextRoot !== this) {
                scopes.push(this.contextRoot);
            }

            const seen = new Set();
            const targets = [];

            scopes.forEach(scope => {
                if (!scope?.querySelectorAll) return;
                scope.querySelectorAll('[data-quantity-label]').forEach(element => {
                    if (seen.has(element)) return;
                    seen.add(element);
                    targets.push(element);
                });
            });

            return targets;
        }

        renderSubscriptionPrices(data) {
            const currentPrice = this.querySelector('.subscription-option--subscribe .subscription-option__price.price--current');
            if (currentPrice) this.setPriceText(currentPrice, data.priceSubscription);

            const comparePrice = this.querySelector('.subscription-option--subscribe .subscription-option__price.price--regular');
            const shouldShowCompare = data.compareSubscription > data.priceSubscription && data.compareSubscription > 0;
            this.togglePriceElement(comparePrice, shouldShowCompare, data.compareSubscription);
        }

        renderOnetimePrices(data) {
            const currentPrice = this.querySelector('.subscription-option--onetime .subscription-option__price.price--current');
            if (currentPrice) this.setPriceText(currentPrice, data.priceOnetime);

            const comparePrice = this.querySelector('.subscription-option--onetime .subscription-option__price.price--regular');
            const shouldShowCompare = data.compareOnetime > data.priceOnetime && data.compareOnetime > 0;
            this.togglePriceElement(comparePrice, shouldShowCompare, data.compareOnetime);
        }

        renderSavings(data) {
            const savingsPrice = this.querySelector('.subscription-option__savings-amount .savings-amount__price');
            if (!savingsPrice) return;

            const normalizedSavings = Math.max(0, data.savingsSubscription || 0);
            this.setPriceText(savingsPrice, normalizedSavings);
        }

        renderSummarySavings(data, purchaseType) {
            if (!this.contextRoot) return;

            const summary = this.contextRoot.querySelector('.subscription-form__savings-amount');
            if (!summary) return;

            const isSubscription = purchaseType === 'subscription';
            this.setBlockVisibility(summary, isSubscription);
            if (!isSubscription) return;

            const labelSpan = summary.querySelector('.subscription-form__savings-label');
            const valueSpan = summary.querySelector('.subscription-form__savings-value');
            const compareSpan = summary.querySelector('.subscription-form__price-compare');
            const currentSpan = summary.querySelector('.subscription-form__price-current');

            if (labelSpan) {
                const labelText = summary.dataset.savingsLabel || labelSpan.textContent || '';
                labelSpan.textContent = labelText;
            }

            const currentCents = isSubscription ? data.priceSubscription : data.priceOnetime;
            const compareCents = isSubscription ? data.compareSubscription : data.compareOnetime;
            const savingsCents = isSubscription ? data.savingsSubscription : Math.max(0, compareCents - currentCents);

            if (valueSpan) this.setPriceText(valueSpan, savingsCents);

            const shouldShowCompare = compareCents > currentCents && compareCents > 0;
            this.togglePriceElement(compareSpan, shouldShowCompare, compareCents);

            if (currentSpan) this.setPriceText(currentSpan, currentCents);
        }


        setPriceText(element, cents) {
            if (!element) return;

            const formatted = this.formatMoney(cents);
            element.innerHTML = formatted;
        }

        togglePriceElement(element, shouldShow, cents) {
            if (!element) return;

            this.cacheOriginalDisplay(element);
            element.toggleAttribute('hidden', !shouldShow);
            element.setAttribute('aria-hidden', (!shouldShow).toString());
            element.style.display = shouldShow ? (element.dataset.originalDisplay || '') : 'none';

            if (shouldShow) {
                this.setPriceText(element, cents);
            }
        }

        cacheOriginalDisplay(element) {
            if (!element || element.dataset.originalDisplay) return;

            const computedDisplay = window.getComputedStyle(element).display;
            element.dataset.originalDisplay = computedDisplay !== 'none' ? computedDisplay : '';
        }

        formatMoney(cents) {
            if (typeof cents === 'string') {
                cents = cents.replace('.', '');
            }

            let value = parseInt(cents, 10);

            if (!Number.isFinite(value)) {
                value = 0;
            }

            const formatString = this.moneyFormat || '${{amount}}';
            const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
            const match = formatString.match(placeholderRegex);
            const formatType = match ? match[1] : 'amount';

            const formatWithDelimiters = (number, precision, thousands, decimal) => {
                thousands = thousands || ',';
                decimal = decimal || '.';

                if (isNaN(number) || number === null) {
                    return 0;
                }

                number = (number / 100).toFixed(precision);

                const parts = number.split('.');
                const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
                const centsAmount = parts[1] ? decimal + parts[1] : '';

                return dollarsAmount + centsAmount;
            };

            let formattedValue;

            switch (formatType) {
                case 'amount':
                    formattedValue = formatWithDelimiters(value, 2);
                    break;
                case 'amount_no_decimals':
                    formattedValue = formatWithDelimiters(value, 0);
                    break;
                case 'amount_with_comma_separator':
                    formattedValue = formatWithDelimiters(value, 2, '.', ',');
                    break;
                case 'amount_no_decimals_with_comma_separator':
                    formattedValue = formatWithDelimiters(value, 0, '.', ',');
                    break;
                case 'amount_with_apostrophe_separator':
                    formattedValue = formatWithDelimiters(value, 2, "'", '.');
                    break;
                default:
                    formattedValue = formatWithDelimiters(value, 2);
                    break;
            }

            return formatString.replace(placeholderRegex, formattedValue);
        }

        getBasePrices() {
            const toInt = (value, fallback = 0) => {
                const parsed = parseInt(value, 10);
                return Number.isFinite(parsed) ? parsed : fallback;
            };

            const priceOnetime = toInt(this.dataset.priceOnetime);
            const priceSubscription = toInt(this.dataset.priceSubscription, priceOnetime);
            const compareOnetime = toInt(this.dataset.compareOnetime, priceOnetime);
            const compareSubscription = toInt(this.dataset.compareSubscription, compareOnetime || priceSubscription);
            const pricePerServingOnetime = toInt(this.dataset.pricePerServingOnetime);
            const pricePerServingSubscription = toInt(this.dataset.pricePerServingSubscription, pricePerServingOnetime);
            let savingsSubscription = toInt(this.dataset.savingsSubscription);

            if (!savingsSubscription && compareSubscription > priceSubscription) {
                savingsSubscription = compareSubscription - priceSubscription;
            }

            return {
                priceOnetime,
                priceSubscription,
                compareOnetime,
                compareSubscription,
                savingsSubscription,
                pricePerServingOnetime,
                pricePerServingSubscription
            };
        }

        getBaseServingsNumber() {
            const parseValue = (value) => {
                const parsed = parseInt(value, 10);
                return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
            };

            const fromDataset = parseValue(this.dataset.servingsNumber);
            if (fromDataset) return fromDataset;

            const fallbackTarget = this.servingsTargets[0];
            if (fallbackTarget) {
                const fromDom = parseValue(fallbackTarget.textContent);
                if (fromDom) return fromDom;
            }

            return 0;
        }

        initSubscribeBar() {
            if (!this.productPage) return;

            const subscribeBar = this.productPage.querySelector('.subscribe-bar');
            if (!subscribeBar) return;

            subscribeBar.classList.add('subscribe-bar--inited');
        }

        showPopup() {
            const popup = document.querySelector('.subscribe_save_popup');
            if (!popup) return;

            popup.style.display = 'flex';
            popup.querySelectorAll('.close_btn, .subscribe_gotit').forEach(closeButton => {
                closeButton.addEventListener('click', () => {
                    popup.style.display = 'none';
                }, { once: true });
            });
        }

    }

    window.customElements.define('subscription-form', SubscriptionForm);
}
